import React, {useState, useEffect, useCallback, useRef} from 'react';

import {IconButton, Text} from '@albert-io/atomic';
import albeyZuriLearnWrite from 'assets/images/albey/albey_zuri_learn_write.svg';

import starAnimation from './assets/star_animation.json';
import educatorBg from './assets/educator_bg.svg';
import classNames from 'classnames';
import Lottie from 'lottie-react';

interface QuoteCardData {
  id: string;
  quote: React.ReactNode;
  name: string;
  location: string;
}

const QUOTES: QuoteCardData[] = [
  {
    id: 'matthew',
    quote: (
      <>
        &ldquo;Students are able to try different questions from a range of subjects and practice
        their skills on their own time.{' '}
        <span className='font-bold'>
          I highly recommend Albert for any new teacher who is just learning how to incorporate more
          technology in the classroom.
        </span>
        &rdquo;
      </>
    ),
    name: 'Matthew Marin',
    location: 'IDEA Public Schools, Texas'
  },
  {
    id: 'sarah',
    quote: (
      <>
        &ldquo;Albert Adaptive is a{' '}
        <span className='font-bold'>
          fun and easy way to integrate ACT practice and even just grammar practice into daily
          lessons
        </span>
        . You can just pick a skill to assign and do not need to worry about taking time to find
        appropriate questions.&rdquo;
      </>
    ),
    name: 'Sarah Johnson',
    location: 'Beloit School District, Wisconsin'
  },
  {
    id: 'cathy',
    quote: (
      <>
        &ldquo;I love that I can get them <span className='font-bold'>instant feedback</span> on the
        questions that they are assigned. Even when they have questions, it&apos;s great because{' '}
        <span className='font-bold'>the feedback gets them talking and thinking.</span>&rdquo;
      </>
    ),
    name: 'Cathy Trosclair',
    location: 'Terrebonne Parish School, Louisiana'
  },
  {
    id: 'caren',
    quote: (
      <>
        &ldquo;
        <span className='font-bold'>
          The instant feedback my students get that goes above and beyond a green check mark
        </span>
        . I love the data. I love how questions are searchable by standard codes and
        keywords.&rdquo;
      </>
    ),
    name: 'Caren Saunders',
    location: 'Kent County Public Schools, Maryland'
  },
  {
    id: 'john',
    quote: (
      <>
        &ldquo;Albert gives teachers the <span className='font-bold'>diagnostic tools</span> to see
        where instruction is effective, and where instruction needs to be reviewed. It also allows
        teachers to <span className='font-bold'>individually meet our students needs.</span>&rdquo;
      </>
    ),
    name: 'John Crawford',
    location: 'Marion County Public Schools, Florida'
  }
];

const DESKTOP_CARD_WIDTH_CQW = 80;
const DESKTOP_CARD_GAP_PX = 40;
const AUTO_ROTATE_MS = 5000;
const EXTENDED_QUOTES = [...QUOTES, ...QUOTES, ...QUOTES];

function QuoteCard({
  data,
  isActive,
  mobile
}: {
  data: QuoteCardData;
  isActive: boolean;
  mobile?: boolean;
}) {
  return (
    <div
      className={classNames(
        'relative flex shrink-0 flex-col gap-x3 rounded-x2 border border-primary background-primary py-x2 pr-x5 pl-x3 transition-opacity duration-300',
        {
          'opacity-100': isActive,
          'opacity-85': !isActive,
          'h-full w-full justify-between text-center': mobile,
          'h-full w-full': !mobile
        }
      )}
    >
      <Text size={mobile ? 'regular' : 'l'}>{data.quote}</Text>
      <div className='mt-auto flex flex-col'>
        <Text size='s' color='secondary'>
          {data.name}
        </Text>
        <Text size='s' color='secondary'>
          {data.location}
        </Text>
      </div>
      <div className='absolute top-[-66px] right-[-67px] z-10'>
        <Lottie animationData={starAnimation} loop className='rotate-12' />
      </div>
    </div>
  );
}

function PaginationDots({count, activeIndex}: {count: number; activeIndex: number}) {
  return (
    <div className='flex items-center justify-center gap-x1'>
      {Array.from({length: count}, (_, i) => (
        <div
          key={i}
          className={classNames(
            'h-x1-quarter w-x1-quarter rounded-full bg-neutral-1000 transition-colors',
            {
              'opacity-32': i === activeIndex,
              'opacity-24': i !== activeIndex
            }
          )}
        />
      ))}
    </div>
  );
}

function useCarousel(count: number) {
  const [pos, setPos] = useState(count);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAutoRotate = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setIsTransitioning(true);
      setPos((p) => p + 1);
    }, AUTO_ROTATE_MS);
  }, []);

  useEffect(() => {
    startAutoRotate();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startAutoRotate]);

  const handleTransitionEnd = useCallback(() => {
    if (pos >= count * 2) {
      setIsTransitioning(false);
      setPos(pos - count);
    } else if (pos < count) {
      setIsTransitioning(false);
      setPos(pos + count);
    }
  }, [pos, count]);

  const goTo = (direction: 'prev' | 'next') => {
    setIsTransitioning(true);
    setPos((p) => (direction === 'next' ? p + 1 : p - 1));
    startAutoRotate();
  };

  const realIndex = ((pos % count) + count) % count;

  return {pos, isTransitioning, handleTransitionEnd, goTo, realIndex};
}

/**
 * Desktop carousel
 */
export default function SignUpCarousel() {
  const count = QUOTES.length;
  const {pos, isTransitioning, handleTransitionEnd, goTo} = useCarousel(count);

  // Translate by (card_width + gap)
  const slideStep = `calc(${pos} * (${DESKTOP_CARD_WIDTH_CQW}cqw + ${DESKTOP_CARD_GAP_PX}px) * -1)`;

  return (
    <div className='relative flex h-full flex-col'>
      <img
        src={educatorBg}
        alt=''
        aria-hidden='true'
        className='inset-0 pointer-events-none absolute h-full w-full object-cover'
      />

      <div className='relative flex flex-1 flex-col justify-center py-x7 pl-x5'>
        <img
          src={albeyZuriLearnWrite}
          alt=''
          aria-hidden='true'
          className='mb-x5 w-[clamp(206px,calc(25vw-100px),420px)]'
        />

        <Text className='text-[32px] font-black' color='primary-inverse'>
          What educators are saying
        </Text>

        <div className='flex flex-col gap-x2'>
          <div className='@container overflow-x-clip overflow-y-visible pt-x1-half'>
            <div
              className={`flex items-stretch gap-x5 ${isTransitioning ? 'transition-transform duration-500 ease-in-out' : ''}`}
              style={{transform: `translateX(${slideStep})`}}
              onTransitionEnd={handleTransitionEnd}
              role='region'
              aria-label='Educator quotes'
            >
              {EXTENDED_QUOTES.map((quote, i) => (
                <div key={`${quote.id}-${i}`} className='w-[80cqw] shrink-0'>
                  <QuoteCard data={quote} isActive={i === pos} />
                </div>
              ))}
            </div>
          </div>

          <div className='flex gap-x1'>
            <IconButton
              color='blend-dark'
              variant='solid'
              size='s'
              icon='arrow-left'
              label='Previous quote'
              onClick={() => goTo('prev')}
            />
            <IconButton
              color='blend-dark'
              variant='solid'
              size='s'
              icon='arrow-right'
              label='Next quote'
              onClick={() => goTo('next')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Mobile carousel
 */
export function SignUpCarouselMobile() {
  const count = QUOTES.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchDeltaX = useRef(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };

  const handleTouchEnd = () => {
    const threshold = 50;
    if (touchDeltaX.current < -threshold) {
      setActiveIndex((prev) => (prev + 1) % count);
    } else if (touchDeltaX.current > threshold) {
      setActiveIndex((prev) => (prev - 1 + count) % count);
    }
  };

  return (
    <div className='relative flex flex-col background-brand-bold'>
      <img
        src={educatorBg}
        alt=''
        aria-hidden='true'
        className='inset-0 pointer-events-none absolute h-full w-full object-cover'
      />

      <div className='relative flex flex-col items-center gap-x3 px-x4 py-x5'>
        <Text className='text-xxl font-black' color='primary-inverse'>
          What educators are saying
        </Text>

        <div
          className='grid w-full touch-pan-y'
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          role='region'
          aria-label='Educator quotes'
        >
          {QUOTES.map((quote, i) => (
            <div
              key={quote.id}
              className={classNames('col-start-1 row-start-1 transition-opacity duration-300', {
                'opacity-100': i === activeIndex,
                'pointer-events-none opacity-0': i !== activeIndex
              })}
              aria-hidden={i !== activeIndex}
            >
              <QuoteCard data={quote} isActive mobile />
            </div>
          ))}
        </div>

        <PaginationDots count={count} activeIndex={activeIndex} />
      </div>
    </div>
  );
}
